import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Html5Qrcode } from "html5-qrcode";
import { BadgeCheck, XCircle, ScanLine, CheckCheck, Phone, ShieldAlert, Loader2 } from "lucide-react";
import { passTokenFromText } from "@/lib/eventLib";
import { ContactLinks } from "@/components/events/ContactNumbers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicScanner({ token }) {
  const [info, setInfo] = useState(null);
  const [infoErr, setInfoErr] = useState("");
  const [phase, setPhase] = useState("scan"); // scan | fetched | checked
  const [pass, setPass] = useState(null);
  const [checkRes, setCheckRes] = useState(null);
  const [scanToken, setScanToken] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);
  const runningRef = useRef(false);
  const phaseRef = useRef("scan");

  useEffect(() => {
    setInfoErr("");
    setInfo(null);
    axios.get(`${API}/public/scanner/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setInfoErr(e.response?.data?.detail || "Scanner link active nahi hai"));
  }, [token]);

  const setPhaseSync = (p) => { phaseRef.current = p; setPhase(p); };

  const stop = async () => {
    if (scannerRef.current && runningRef.current) {
      try { await scannerRef.current.stop(); } catch { /* noop */ }
      try { scannerRef.current.clear(); } catch { /* noop */ }
      runningRef.current = false;
    }
  };
  const startCamera = async () => {
    const el = scannerRef.current;
    if (!el || runningRef.current) return;
    try {
      let config = { facingMode: { ideal: "environment" } };
      try {
        const cams = await Html5Qrcode.getCameras();
        if (cams && cams.length) {
          const back = cams.find((c) => /back|rear|environment/i.test(c.label)) || cams[cams.length - 1];
          config = { deviceId: { exact: back.id } };
        }
      } catch { /* fallback */ }
      await el.start(config, { fps: 10, qrbox: { width: 230, height: 230 } }, onScan, () => {});
      runningRef.current = true;
      setErr("");
    } catch (e) {
      const name = e?.name || "";
      setErr(name === "NotAllowedError" ? "Camera permission block hai — browser/phone settings me Camera ALLOW karein, phir Retry dabayein." : `Camera start nahi ho paya (${name || "unknown"}). Retry dabayein.`);
    }
  };

  const onScan = async (text) => {
    if (phaseRef.current !== "scan") return;
    phaseRef.current = "busy";
    const t = passTokenFromText(text);
    if (!t) { phaseRef.current = "scan"; setErr("Ye QR kisi Entry Pass ka nahi lagta"); return; }
    setErr("");
    await stop();
    setScanToken(t);
    try {
      const r = await axios.get(`${API}/public/scanner/${token}/pass/${t}`);
      setPass(r.data);
      setPhaseSync("fetched");
    } catch (e) {
      setErr(e.response?.status === 403 ? (e.response?.data?.detail || "Ye Entry Pass is event ka nahi hai") : "Pass nahi mila / invalid");
      setPhaseSync("scan");
      startCamera();
    }
  };

  useEffect(() => {
    if (!info || infoErr) return;
    const el = new Html5Qrcode("pub-qr-reader");
    scannerRef.current = el;
    startCamera();
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, infoErr]);

  const nextScan = () => { setPass(null); setCheckRes(null); setErr(""); setPhaseSync("scan"); startCamera(); };

  const doCheck = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/scanner/${token}/checkin`, { pass_token: scanToken });
      setCheckRes(r.data);
      setPhaseSync("checked");
    } catch (e) {
      setErr(e.response?.status === 403 ? (e.response?.data?.detail || "Ye Entry Pass is event ka nahi hai") : "Check failed");
      setPhaseSync("scan");
      startCamera();
    } finally {
      setBusy(false);
    }
  };

  if (infoErr) return <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6"><div className="text-center"><img src="/logo.webp?v=2" alt="" className="h-10 mx-auto mb-3" /><p className="text-red-400 font-semibold" data-testid="pub-scan-error">{infoErr}</p></div></div>;
  if (!info) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>;

  const valid = checkRes ? checkRes.valid : (pass && pass.status === "approved");

  return (
    <div className="min-h-screen bg-slate-900" data-testid="public-scanner">
      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        <div className="text-white text-center space-y-0.5">
          <p className="font-heading font-bold flex items-center justify-center gap-2"><ScanLine className="h-5 w-5" /> Entry Pass Scanner</p>
          <p className="text-sm font-semibold text-white/80" data-testid="pub-scan-event-name">{info.name}</p>
          {(info.city || info.event_date) && <p className="text-xs text-white/60">{[info.city, info.event_date].filter(Boolean).join(" · ")}</p>}
        </div>

        {phase === "scan" && (
          <div className="space-y-3">
            <div id="pub-qr-reader" className="rounded-2xl overflow-hidden bg-black border border-white/10" data-testid="pub-qr-reader" />
            {err && (
              <div className="space-y-2 text-center" data-testid="pub-scan-err">
                <div className="flex items-center gap-2 justify-center text-red-400 text-sm"><ShieldAlert className="h-4 w-4 shrink-0" />{err}</div>
                <Button size="sm" onClick={startCamera} data-testid="pub-scan-retry-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5"><ScanLine className="h-4 w-4" /> Retry Camera</Button>
              </div>
            )}
            <p className="text-white/60 text-xs text-center">Entry Pass ka QR camera ke saamne rakhein. Sirf isi event ke pass check honge.</p>
          </div>
        )}

        {phase === "fetched" && pass && (
          <div className="bg-white rounded-2xl p-6 space-y-4 text-center" data-testid="pub-scan-fetched">
            {pass.data && Object.values(pass.data).some((v) => typeof v === "string" && v.startsWith("data:image")) && (
              <img src={Object.values(pass.data).find((v) => typeof v === "string" && v.startsWith("data:image"))} alt="" className="h-20 w-20 rounded-xl object-cover border mx-auto" />
            )}
            <p className="text-xs uppercase tracking-widest text-slate-400">Pass mila — Check karein</p>
            {pass.serial_no > 0 && (
              <p className="inline-block text-xl font-extrabold tracking-wide text-cyan-700 bg-cyan-50 border-2 border-cyan-300 rounded-xl px-5 py-2.5" data-testid="pub-scan-serial-no">
                SERIAL NO. {pass.serial_prefix || ""}{String(pass.serial_no).padStart(3, "0")}
              </p>
            )}
            {pass.tag && <p className="inline-block text-lg font-extrabold tracking-wide text-amber-800 bg-amber-100 border-2 border-amber-300 rounded-xl px-5 py-2" data-testid="pub-scan-tag">{pass.tag}</p>}
            <p className="font-heading text-2xl font-bold" data-testid="pub-scan-cand-name">{pass.candidate_name}</p>
            {pass.event_total > 0 && <p className="text-xs font-bold text-cyan-700" data-testid="pub-scan-entry-count">{pass.event_checked_in} / {pass.event_total} entries ho chuki</p>}
            {pass.checked_in && <p className="text-xs font-bold text-amber-600 bg-amber-50 rounded-full px-3 py-1 inline-block">Pehle entry check ho chuki hai</p>}
            <Button onClick={doCheck} disabled={busy} data-testid="pub-scan-check-btn" className="w-full h-11 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold gap-2">
              <CheckCheck className="h-5 w-5" /> {busy ? "Checking..." : "Check Entry"}
            </Button>
            <Button variant="outline" onClick={nextScan} className="w-full" data-testid="pub-scan-again-btn">Dobara Scan</Button>
          </div>
        )}

        {phase === "checked" && pass && (
          <div className="space-y-3">
            <div className={`rounded-2xl p-8 text-center space-y-3 ${valid ? "bg-emerald-500" : "bg-red-500"}`} data-testid="pub-scan-result">
              {valid ? <BadgeCheck className="h-16 w-16 mx-auto text-white" /> : <XCircle className="h-16 w-16 mx-auto text-white" />}
              <p className="font-heading text-2xl font-bold text-white" data-testid="pub-scan-verdict">{valid ? "ENTRY VALID" : "ENTRY INVALID"}</p>
              {checkRes?.already && <p className="text-white/90 text-xs font-bold bg-white/20 rounded-full px-3 py-1 inline-block" data-testid="pub-scan-already">Pehle se checked-in tha</p>}
              {pass.serial_no > 0 && (
                <p className="inline-block text-xl font-extrabold tracking-wide text-white bg-white/20 border-2 border-white/40 rounded-xl px-5 py-2" data-testid="pub-scan-serial-result">
                  SERIAL NO. {pass.serial_prefix || ""}{String(pass.serial_no).padStart(3, "0")}
                </p>
              )}
              {pass.tag && <p className="inline-block text-lg font-extrabold tracking-wide bg-amber-300 text-amber-950 border-2 border-amber-200 rounded-xl px-5 py-2" data-testid="pub-scan-tag-result">{pass.tag}</p>}
              <p className="text-white/90 font-semibold">{pass.candidate_name}</p>
              {checkRes?.event_total > 0 && <p className="text-white font-bold text-sm bg-white/20 rounded-full px-3 py-1 inline-block" data-testid="pub-scan-count-checked">{checkRes.event_checked_in} / {checkRes.event_total} entries ho chuki</p>}
              {pass.contact_number && <ContactLinks value={pass.contact_number} className="text-white/90 text-sm underline justify-center" testId="pub-scan-contact" />}
            </div>
            <Button onClick={nextScan} data-testid="pub-scan-next-btn" className="w-full h-11 bg-white text-slate-900 hover:bg-slate-100 font-semibold gap-2">
              <ScanLine className="h-5 w-5" /> Next Scan
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
